import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { useNavigate } from "react-router-dom";

const KIND_STYLES = {
  compliance: { label: "Compliance", bg: "rgba(114,190,68,0.15)", fg: "#145353" },
  milestone: { label: "Milestone", bg: "rgba(14,165,233,0.14)", fg: "#0369A1" },
  invoice: { label: "Invoice", bg: "rgba(245,158,11,0.16)", fg: "#92400E" },
  task: { label: "Task", bg: "rgba(139,92,246,0.14)", fg: "#6D28D9" },
  pmr: { label: "PMR", bg: "rgba(20,83,83,0.12)", fg: "#145353" },
  mep: { label: "MEP", bg: "rgba(20,83,83,0.12)", fg: "#145353" },
  systems: { label: "System", bg: "rgba(20,83,83,0.12)", fg: "#145353" },
};

export default function CalendarPage() {
  const nav = useNavigate();
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);

  const monthKey = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;

  useEffect(() => {
    api.get("/calendar/events", { params: { month: monthKey } }).then((r) => setEvents(r.data)).catch(console.error);
    setSelected(null);
  }, [monthKey]);

  const byDate = useMemo(() => {
    const m = {};
    for (const e of events) (m[e.date] = m[e.date] || []).push(e);
    return m;
  }, [events]);

  const weeks = useMemo(() => {
    const first = new Date(cursor);
    const startOffset = (first.getDay() + 6) % 7; // Monday-first
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7) cells.push(null);
    const w = [];
    for (let i = 0; i < cells.length; i += 7) w.push(cells.slice(i, i + 7));
    return w;
  }, [cursor]);

  const today = new Date().toISOString().slice(0, 10);
  const dateStr = (d) => `${monthKey}-${String(d).padStart(2, "0")}`;

  return (
    <div className="space-y-5 animate-fade-up" data-testid="calendar-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold mb-1" style={{ color: "#72BE44" }}>Schedule</p>
          <h1 className="text-3xl font-bold tracking-tight text-stone-900 flex items-center gap-3">
            <CalendarDays className="w-7 h-7" style={{ color: "#145353" }} />
            {cursor.toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="cal-prev"
            className="p-2 bg-white border border-stone-200 rounded-lg hover:border-[#72BE44]"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => { const d = new Date(); setCursor(new Date(d.getFullYear(), d.getMonth(), 1)); }} data-testid="cal-today"
            className="px-4 py-2 bg-white border border-stone-200 rounded-lg text-sm font-semibold hover:border-[#72BE44]" style={{ color: "#145353" }}>Today</button>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="cal-next"
            className="p-2 bg-white border border-stone-200 rounded-lg hover:border-[#72BE44]"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-[10px]">
        {Object.entries(KIND_STYLES).slice(0, 4).map(([k, s]) => (
          <span key={k} className="px-2 py-1 rounded-full font-bold uppercase tracking-wider" style={{ background: s.bg, color: s.fg }}>{s.label}</span>
        ))}
      </div>

      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-7 border-b border-stone-200 bg-stone-50">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div key={d} className="px-2 py-2 text-[10px] uppercase tracking-wider font-bold text-stone-500 text-center">{d}</div>
          ))}
        </div>
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 border-b border-stone-100 last:border-b-0">
            {week.map((d, di) => {
              const ds = d ? dateStr(d) : null;
              const evs = ds ? byDate[ds] || [] : [];
              const isToday = ds === today;
              return (
                <button key={di} disabled={!d} onClick={() => setSelected(ds)}
                  data-testid={d ? `cal-day-${ds}` : undefined}
                  className={`min-h-[86px] p-1.5 border-r border-stone-100 last:border-r-0 text-left align-top transition ${d ? "hover:bg-stone-50" : "bg-stone-50/40"} ${selected === ds ? "ring-2 ring-inset ring-[#72BE44]" : ""}`}>
                  {d && (
                    <>
                      <div className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full ${isToday ? "text-white" : "text-stone-600"}`}
                        style={isToday ? { background: "#72BE44" } : {}}>{d}</div>
                      <div className="space-y-0.5 mt-1">
                        {evs.slice(0, 3).map((e, i) => {
                          const s = KIND_STYLES[e.kind] || KIND_STYLES.task;
                          return <div key={i} className="text-[9px] px-1 py-0.5 rounded truncate font-medium" style={{ background: s.bg, color: s.fg }}>{e.label}</div>;
                        })}
                        {evs.length > 3 && <div className="text-[9px] text-stone-400 font-bold">+{evs.length - 3} more</div>}
                      </div>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {selected && (
        <div className="bg-white border border-stone-200 rounded-xl p-5" data-testid="cal-day-detail">
          <div className="text-sm font-bold mb-3" style={{ color: "#145353" }}>
            {new Date(selected).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })} — {(byDate[selected] || []).length} item(s)
          </div>
          <div className="space-y-1.5">
            {(byDate[selected] || []).map((e, i) => {
              const s = KIND_STYLES[e.kind] || KIND_STYLES.task;
              return (
                <button key={i} onClick={() => e.link && nav(e.link)}
                  className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg border border-stone-100 hover:border-[#72BE44] transition">
                  <span className="text-[9px] uppercase font-bold px-2 py-0.5 rounded-full shrink-0" style={{ background: s.bg, color: s.fg }}>{s.label}</span>
                  <span className="text-sm text-stone-700 truncate">{e.label}</span>
                </button>
              );
            })}
            {(byDate[selected] || []).length === 0 && <div className="text-sm text-stone-400">Nothing scheduled this day.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
